import httpService from '@/api/http';
import { LOADING_KEY } from '@/util/constants';

/**
 * @see {@link https://documenter.getpostman.com/view/3171682/RzfZNY7x?version=latest#c8160c13-8e53-452a-b74d-1cbaa0e7a8ba}
 *
 * @param context
 *
 * @return {Promise<AxiosResponse<any>>}
 */
function venueFetchRecipes(
  context,
  { limit = 10, filters = {}, page = 1, loadingKey = LOADING_KEY.VENUE_FETCH_RECIPES } = {},
) {
  return httpService.get('/venue/recipes', {
    headers: {
      loadingKey,
    },
    params: {
      limit,
      page,
      ...filters,
    },
  });
}

/**
 * @see {@link https://documenter.getpostman.com/view/3171682/RzfZNY7x?version=latest#4f6d55c3-22dc-4ba4-8477-9522384d0dc5}
 *
 * @param context
 *
 * @return {Promise<AxiosResponse<any>>}
 */
function venueFetchRecipe(context, { id, loadingKey = LOADING_KEY.VENUE_FETCH_RECIPES } = {}) {
  return httpService.get(`/venue/recipes/${id}`, {
    headers: {
      loadingKey,
    },
  });
}

/**
 * @since 2.3.0
 * @see {@link https://documenter.getpostman.com/view/3171682/RzfZNY7x?version=latest#75903659-0a04-4d44-afe2-050b1036fe49}
 *
 * @param {number} id
 * @param {Array} locationIds
 * @param {string} loadingKey

 * @return {Promise<AxiosResponse<T>>}
 */
function venueUpdateRecipeLocations(
  context,
  { id, locationIds, loadingKey = LOADING_KEY.VENUE_UPDATE_RECIPE },
) {
  return httpService.put(
    `/venue/recipes/${id}/visibility`,
    {
      visibleForVenueIdsFromGroup: locationIds,
    },
    {
      headers: {
        loadingKey,
      },
    },
  );
}

/**
 * @since 2.3.0
 * @see {@link https://documenter.getpostman.com/view/3171682/RzfZNY7x?version=latest#20818622-72e1-4baa-9995-c6dc8f4e9f2b}
 *
 * @param {Object} body
 * @param {string} loadingKey

 * @return {Promise<AxiosResponse<T>>}
 */
function venueCreateRecipe(context, { loadingKey = LOADING_KEY.VENUE_CREATE_RECIPE, body }) {
  return httpService.post(`/venue/recipes`, body, {
    headers: {
      loadingKey,
    },
  });
}

/**
 * @since 2.3.0
 * @see {@link https://documenter.getpostman.com/view/3171682/RzfZNY7x?version=latest#75903659-0a04-4d44-afe2-050b1036fe49}
 *
 * @param {number} id
 * @param {Array} body
 * @param {string} loadingKey

 * @return {Promise<AxiosResponse<T>>}
 */
function venueUpdateRecipe(context, { body, id, loadingKey = LOADING_KEY.VENUE_UPDATE_RECIPE }) {
  return httpService.put(`/venue/recipes/${id}`, body, {
    headers: {
      loadingKey,
    },
  });
}

/**
 * @see {@link https://documenter.getpostman.com/view/3171682/RzfZNY7x?version=latest#c8160c13-8e53-452a-b74d-1cbaa0e7a8ba}
 *
 * @param context
 * @param string limit
 * @param {Object} filters
 *
 * @return {Promise<AxiosResponse<any>>}
 */
function venueFetchRecipeItems(
  context,
  { limit = 10, term, loadingKey = LOADING_KEY.VENUE_FETCH_RECIPE_ITEMS } = {},
) {
  return httpService.get('/venue/recipes/search/recipe-items', {
    headers: {
      loadingKey,
    },
    params: {
      ...(limit ? { limit } : {}),
      ...(term ? { term } : {}),
    },
  });
}

/**
 * @since 2.3.0
 * @see {@link https://documenter.getpostman.com/view/3171682/RzfZNY7x?version=latest#4b759f42-acbd-49d5-abd8-218fc31b3717}
 *
 * @param {string} loadingKey

 * @return {Promise<AxiosResponse<T>>}
 */
function venueDeleteRecipe(context, { loadingKey = LOADING_KEY.VENUE_DELETE_RECIPE, id }) {
  return httpService.delete(`/venue/recipes/${id}`, {
    headers: {
      loadingKey,
    },
  });
}

/**
 * @since 2.3.0
 * Fetch available recipe types
 *
 * @param {string} loadingKey
 * @return {Promise<AxiosResponse<T>>}
 */
function venueFetchRecipeTypes(context, { loadingKey = LOADING_KEY.VENUE_FETCH_RECIPE_TYPES } = {}) {
  return httpService.get('/venue/recipes/types', {
    headers: {
      loadingKey,
    },
  });
}

/**
 * Upload CSV for recipes.
 *
 * @param context
 * @param {FormData} data
 * @return {Promise<AxiosResponse<any>>}
 */
function venueUploadRecipeCSV(context, { data }) {
  const config = { headers: { 'Content-Type': 'multipart/form-data' } };
  return httpService.put(`/venue/recipes/import`, data, config);
}

/**
 * Export recipes to CSV.
 *
 * @param context
 * @param {Object} params - Filter parameters
 * @return {Promise<AxiosResponse<any>>}
 */
function venueExportRecipeCSV(context, { params = {} } = {}) {
  return httpService.get(`/venue/recipes/export`, {
    responseType: 'blob',
    params,
    headers: {
      loadingKey: LOADING_KEY.VENUE_EXPORT_RECIPES,
    },
  });
}

/**
 * Get recipe import template.
 *
 * @param context
 * @return {Promise<AxiosResponse<any>>}
 */
// eslint-disable-next-line no-unused-vars
function venueGetRecipeImportTemplate(context) {
  return httpService.get(`/venue/recipes/import-template`, {
    responseType: 'blob',
  });
}


export default {
  venueFetchRecipes,
  venueFetchRecipe,
  venueUpdateRecipeLocations,
  venueCreateRecipe,
  venueFetchRecipeItems,
  venueDeleteRecipe,
  venueUpdateRecipe,
  venueFetchRecipeTypes,
  venueUploadRecipeCSV,
  venueExportRecipeCSV,
  venueGetRecipeImportTemplate,
};
